import type { Mailbox } from '../api/types';
import { normaliseName } from './folders';

/*
    The search box's little language.

    Everything here compiles to a JMAP FilterCondition, which means the server
    does the work — the alternative for a mailbox with twelve years of mail in it
    is fetching everything and filtering in the browser, which is not a thing
    that can be made fast.

    The operators are Gmail's, because that is what everyone already knows:

        from:ada  to:me  subject:invoice  in:archive  has:attachment
        filename:pdf  is:unread  is:starred  before:2026-01-01  after:2025-06-01
        larger:5mb  smaller:200kb  "exact phrase"  all:

    Anything that isn't an operator is free text, and an unknown operator is
    treated as free text too rather than silently dropping the word — a search
    that quietly ignores part of what you typed is worse than one that finds too
    much.
*/

export interface Token {
    key: string | null;
    value: string;
}

const OPERATORS = new Set([
    'from', 'to', 'cc', 'bcc', 'subject', 'body', 'in', 'has', 'filename', 'is', 'before', 'after', 'larger', 'smaller',
]);

/** Splits on spaces but keeps "quoted phrases" whole. */
export function tokenise(query: string): Token[] {
    const tokens: Token[] = [];
    const pattern = /(\w+):("[^"]*"|\S+)|"([^"]*)"|(\S+)/g;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(query))) {
        const [, key, keyed, quoted, bare] = match;
        if (key && keyed !== undefined) {
            const value = keyed.replace(/^"|"$/g, '');
            if (OPERATORS.has(key.toLowerCase())) tokens.push({ key: key.toLowerCase(), value });
            // Not an operator — "note:" in a subject line, most likely. Keep the
            // whole thing as text rather than losing half of it.
            else tokens.push({ key: null, value: `${key}:${value}` });
        } else if (quoted !== undefined) {
            tokens.push({ key: null, value: quoted });
        } else if (bare !== undefined) {
            tokens.push({ key: null, value: bare });
        }
    }
    return tokens;
}

const SIZES: Record<string, number> = { b: 1, kb: 1024, mb: 1024 * 1024, gb: 1024 * 1024 * 1024 };

function toBytes(raw: string): number | null {
    const match = /^(\d+(?:\.\d+)?)\s*(b|kb|mb|gb)?$/i.exec(raw.trim());
    if (!match) return null;
    const unit = (match[2] ?? 'b').toLowerCase();
    return Math.round(Number(match[1]) * (SIZES[unit] ?? 1));
}

/**
 * A date the user typed, as the instant the server should compare against.
 *
 * `before:` and `after:` are inclusive of the day named, which is what people
 * mean — "before 2026-01-01" should not include the 1st, and "after 2026-01-01"
 * should. Bare years and months are accepted because people type them.
 */
function toDate(raw: string, edge: 'start' | 'end'): string | null {
    const value = raw.trim();
    let at: Date | null = null;

    if (/^\d{4}$/.test(value)) at = new Date(Number(value), edge === 'start' ? 0 : 11, edge === 'start' ? 1 : 31);
    else if (/^\d{4}-\d{2}$/.test(value)) {
        const [y, m] = value.split('-').map(Number);
        at = edge === 'start' ? new Date(y!, m! - 1, 1) : new Date(y!, m!, 0);
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        at = new Date(y!, m! - 1, d!);
    } else {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) at = parsed;
    }

    if (!at) return null;
    if (edge === 'start') at.setHours(0, 0, 0, 0);
    else at.setHours(23, 59, 59, 999);
    return at.toISOString();
}

export interface Compiled {
    filter: Record<string, unknown> | null;
    /** True when `all:` or an `in:` moved the search off the open folder. */
    everywhere: boolean;
    /** Operators the server can't answer — shown so nobody thinks they applied. */
    ignored: string[];
}

/**
 * Compiles the query for a mailbox.
 *
 * Scoped to the open folder by default, which is what a search box sitting above
 * a folder list implies. `all:` or an explicit `in:` widens it.
 */
export function compileSearch(query: string, mailboxId: string | undefined, folders: Mailbox[]): Compiled {
    const tokens = tokenise(query);
    const conditions: Record<string, unknown>[] = [];
    const text: string[] = [];
    const ignored: string[] = [];
    let everywhere = false;
    let scope: string | undefined = mailboxId;

    const findFolder = (name: string): Mailbox | undefined => {
        const wanted = normaliseName(name);
        return (
            folders.find((f) => normaliseName(f.name) === wanted) ??
            folders.find((f) => (f.role ?? '') === wanted) ??
            folders.find((f) => normaliseName(f.name).includes(wanted))
        );
    };

    for (const token of tokens) {
        if (!token.key) {
            if (/^all:?$/i.test(token.value)) everywhere = true;
            else text.push(token.value);
            continue;
        }

        switch (token.key) {
            case 'from':
            case 'to':
            case 'cc':
            case 'bcc':
            case 'subject':
            case 'body':
                conditions.push({ [token.key]: token.value });
                break;

            case 'filename':
                conditions.push({ attachmentName: token.value });
                break;

            case 'in': {
                const folder = findFolder(token.value);
                if (folder) {
                    scope = folder.id;
                    everywhere = true;
                } else {
                    ignored.push(`in:${token.value}`);
                }
                break;
            }

            case 'has':
                if (/^attachments?$/i.test(token.value)) conditions.push({ hasAttachment: true });
                else ignored.push(`has:${token.value}`);
                break;

            case 'is': {
                const what = token.value.toLowerCase();
                if (what === 'unread') conditions.push({ notKeyword: '$seen' });
                else if (what === 'read') conditions.push({ hasKeyword: '$seen' });
                else if (what === 'starred' || what === 'flagged') conditions.push({ hasKeyword: '$flagged' });
                else if (what === 'unstarred') conditions.push({ notKeyword: '$flagged' });
                else if (what === 'draft') conditions.push({ hasKeyword: '$draft' });
                else if (what === 'answered' || what === 'replied') conditions.push({ hasKeyword: '$answered' });
                else ignored.push(`is:${token.value}`);
                break;
            }

            case 'before': {
                const at = toDate(token.value, 'start');
                if (at) conditions.push({ before: at });
                else ignored.push(`before:${token.value}`);
                break;
            }

            case 'after': {
                const at = toDate(token.value, 'end');
                if (at) conditions.push({ after: at });
                else ignored.push(`after:${token.value}`);
                break;
            }

            case 'larger': {
                const bytes = toBytes(token.value);
                if (bytes !== null) conditions.push({ minSize: bytes });
                else ignored.push(`larger:${token.value}`);
                break;
            }

            case 'smaller': {
                const bytes = toBytes(token.value);
                if (bytes !== null) conditions.push({ maxSize: bytes });
                else ignored.push(`smaller:${token.value}`);
                break;
            }

            default:
                ignored.push(`${token.key}:${token.value}`);
        }
    }

    if (text.length) conditions.push({ text: text.join(' ') });

    // Nothing typed: the plain folder listing.
    if (!conditions.length) {
        return { filter: scope ? { inMailbox: scope } : null, everywhere, ignored };
    }

    if (!everywhere && scope) conditions.unshift({ inMailbox: scope });
    else if (everywhere && scope !== mailboxId && scope) conditions.unshift({ inMailbox: scope });

    return {
        filter: conditions.length === 1 ? conditions[0]! : { operator: 'AND', conditions },
        everywhere,
        ignored,
    };
}

/**
 * The operator someone typed, given the JMAP property a server refused.
 *
 * A server names the condition it can't answer in its own vocabulary —
 * `attachmentName`. Reporting that back verbatim asks the reader to know how
 * their `filename:` was compiled, so it is translated to what they actually
 * wrote before it reaches the screen.
 */
const TYPED_AS: Record<string, string> = {
    attachmentName: 'filename:',
    hasAttachment: 'has:attachment',
    hasKeyword: 'is:',
    notKeyword: 'is:',
    minSize: 'larger:',
    maxSize: 'smaller:',
    before: 'before:',
    after: 'after:',
    inMailbox: 'in:',
    text: '',
};

export function operatorFor(property: string): string {
    return TYPED_AS[property] ?? `${property}:`;
}

export type SortField = 'receivedAt' | 'sentAt' | 'size' | 'from' | 'to' | 'subject';

export interface Sort {
    field: SortField;
    ascending: boolean;
}

export const DEFAULT_SORT: Sort = { field: 'receivedAt', ascending: false };

/** Every sort the server offers, in the order a menu should list them. */
export const SORTS: SortField[] = ['receivedAt', 'sentAt', 'from', 'to', 'subject', 'size'];
